import React, { useCallback, useEffect, useState } from 'react';
import { CreditGroupDto, CreditGroupItemDto } from '../../../../modelView';
import { TabPanel } from '@mui/lab';
import InsuranceDetails from '../../../financeCalculation/insuranceDetails';
import CreditGroupTable from './CreditGroupTable';
import {
  CalculationDomainEnum,
  CalculationResult,
  CreditTypeEnum,
  ParamTypeEnum,
} from '../../../../api/graphql/generated/schema';
import { defaultVat } from '../../../../utils/const';
import { CalculationConfigDto } from '../../../../modelView/finance/calculation/calculationConfig';
import { FormControlLabel, FormGroup, Grid, Paper, Switch, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CardContext, InsuranceConfig } from '../../../../modelView/finance/calculation/interfaces';
import { useCalculateVehicleFinanceLazyQuery } from '../../../../api/graphql/generated/hooks';
import { useDispatch } from 'react-redux';
import { setLoadingStatus } from '../../../../redux/loading';
import { setSnackbar, SnackbarStates } from '../../../../redux/snackbar';
import { getGQLErrorMessages } from '../../../../utils/graphGL/graphQLHelper';
import { differenceInMonths } from 'date-fns';

export const DEFAULT_PAYMENT_PERIODS = [36, 48, 60];
export const DEFAULT_ADVANCE_PAYMENTS = [10, 15, 20, 25, 30, 40, 50];
export const MAX_COMPUTABLE_CREDIT_PERIOD_MONTHS = 60;

const initCalculationInput = {
  domain: CalculationDomainEnum.Credit,
  params: [{ paymentPeriod: 36, advancePayment: 5 }],
};

export const initInsuranceInput: InsuranceConfig = {
  insurance: [],
  deductible: [],
  windshieldLimit: [],
  clientAge: undefined,
  claimFreeMonths: undefined,
};

export const acceptableCalcErrorCodes = ['400', '404'];

/**
 * Calculates the difference in months between the current date and the model year date.
 *
 * @param modelYear
 * @returns
 */
export function getModelMonthDifference(modelYear: number): number {
  const currentDate = new Date();
  const modelDate = new Date(modelYear, currentDate.getMonth(), 1);
  return differenceInMonths(currentDate, modelDate);
}

export default function CreditGroupTab(props: {
  creditGroup: CreditGroupDto;
  cardContext: CardContext;
  domain: CalculationDomainEnum;
  handleUpdate: (creditGroup: CreditGroupDto) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [useCalculator, setUseCalculator] = useState<boolean>(Boolean(props.creditGroup.creditCalculationParams));

  // State for insurance mapping
  const [insuranceConfigOptions, setInsuranceConfigOptions] = useState<InsuranceConfig>(initInsuranceInput);
  const [productDisclaimer, setProductDisclaimer] = useState('');

  // State for tabs input
  const [paymentPeriodOptions, setPaymentPeriodOptions] = useState<Array<number>>([]);
  const [advancePaymentOptions, setAdvancedPaymentOptions] = useState<Array<number>>([]);

  // State for showing calc input
  const [disableCalculatorUsage, setDisableCalculatorUsage] = useState<boolean>(
    getModelMonthDifference(props.cardContext.modelYear) > MAX_COMPUTABLE_CREDIT_PERIOD_MONTHS,
  );

  const [gqlCalculateFinances, { error: errorCalculationInfo, loading: loadingCalculationInfo }] =
    useCalculateVehicleFinanceLazyQuery();

  useEffect(() => {
    dispatch(setLoadingStatus(loadingCalculationInfo));
  }, [loadingCalculationInfo, dispatch]);

  useEffect(() => {
    if (props.cardContext && props.cardContext.modelYear) {
      const monthsDifference = getModelMonthDifference(props.cardContext.modelYear);
      if (monthsDifference > MAX_COMPUTABLE_CREDIT_PERIOD_MONTHS) {
        setDisableCalculatorUsage(true);
        setUseCalculator(false);
      }
    }
  }, [props.cardContext]);

  useEffect(() => {
    if (errorCalculationInfo) {
      if (acceptableCalcErrorCodes.some((code) => errorCalculationInfo.message.includes(code))) {
        setDisableCalculatorUsage(true);
        return;
      }
      dispatch(setSnackbar(true, SnackbarStates.ERROR, getGQLErrorMessages(errorCalculationInfo)));
    }
  }, [errorCalculationInfo, dispatch]);

  const getGQLCardInput = useCallback(() => {
    const [modelKey] = props.cardContext.modelKey.split('/');
    return {
      vehicleType: props.cardContext.vehicleType,
      modelKey: modelKey,
      modelYear: props.cardContext.modelYear,
      totalModelPrice: props.cardContext.totalModelPrice,
      regDateFirst: props.cardContext.regDateFirst,
    };
  }, [props.cardContext]);

  const fetchInsurancePackagesById = useCallback(
    async (insuranceId: number) => {
      const result = await gqlCalculateFinances({
        variables: {
          input: {
            ...initCalculationInput,
            ...getGQLCardInput(),
            insuranceId,
          },
        },
      });

      const calculation = result.data?.calculateVehicleFinance?.calculation;
      if (!calculation) {
        return null;
      }
      const calResult = calculation.calculationResults[0];
      const insurancePackage = calResult.insurancePackageIdParam;
      if (!insurancePackage || !insurancePackage.possibleValues) {
        return null;
      }
      // Build the array of package options
      const packageOptions = insurancePackage.possibleValues.map((pkgId) => ({
        key: pkgId,
        label: insurancePackage.labels ? insurancePackage.labels[String(pkgId)] : String(pkgId),
      }));
      return packageOptions;
    },
    [gqlCalculateFinances, getGQLCardInput],
  );

  const fetchPossibleValues = useCallback(
    async (productId?: string) => {
      const gqlCalculationInfo = await gqlCalculateFinances({
        variables: {
          input: {
            ...initCalculationInput,
            ...getGQLCardInput(),
            productId: productId || undefined,
          },
        },
      });
      const calculations = gqlCalculationInfo.data?.calculateVehicleFinance?.calculation;
      if (!calculations) {
        return;
      }
      setProductDisclaimer(gqlCalculationInfo.data?.calculateVehicleFinance?.productDisclaimer || '');
      const result = calculations.calculationResults[0];
      const insurance = result.insuranceIdParam;
      if (!insurance || !insurance.possibleValues) {
        return;
      }
      let windshieldLimitOptions: Array<number> = [];
      let deductibleOptions: Array<number> = [];

      // For each possible insuranceId, fetch its package list
      const insuranceOptions = await Promise.all(
        insurance.possibleValues.map(async (insuranceId) => {
          const options = await fetchInsurancePackagesById(insuranceId);
          return {
            key: insuranceId,
            label: insurance.labels ? insurance.labels[String(insuranceId)] : String(insuranceId),
            packageOptions: options || [],
          };
        }),
      );

      // payment period available values
      const paymentPeriodParam = result.paymentPeriodParam;
      if (paymentPeriodParam?.possibleValues) {
        if (paymentPeriodParam.paramInputType === ParamTypeEnum.Edit) {
          // no other possible values are set, so default to preset ones
          setPaymentPeriodOptions(DEFAULT_PAYMENT_PERIODS);
        } else {
          setPaymentPeriodOptions(paymentPeriodParam.possibleValues);
        }
      }
      // advance payment available values
      const advancePaymentParam = result.advancePaymentParam;
      if (advancePaymentParam?.possibleValues) {
        setAdvancedPaymentOptions(advancePaymentParam.possibleValues);
      }
      // deductible available values
      const insuranceDeductibleParam = result.insuranceDeductibleParam;
      if (insuranceDeductibleParam?.possibleValues) {
        deductibleOptions = insuranceDeductibleParam.possibleValues;
      }
      // windshield limit available values
      const insuranceWindshieldLimitParam = result.insuranceWindshieldLimitParam;
      if (insuranceWindshieldLimitParam?.possibleValues) {
        windshieldLimitOptions = insuranceWindshieldLimitParam.possibleValues;
      }
      setInsuranceConfigOptions({
        insurance: insuranceOptions,
        deductible: deductibleOptions,
        windshieldLimit: windshieldLimitOptions,
        claimFreeMonths: undefined,
        clientAge:
          [...(result.clientAgeParam?.possibleValues || [])]
            ?.sort((a, b) => b - a)
            ?.map((possibleValue) => ({
              key: possibleValue,
              label: result.clientAgeParam?.labels
                ? result.clientAgeParam.labels[String(possibleValue)]
                : String(possibleValue),
            })) || undefined,
      });
    },
    [gqlCalculateFinances, getGQLCardInput, fetchInsurancePackagesById],
  );

  useEffect(() => {
    if (!disableCalculatorUsage) {
      fetchPossibleValues();
    }
  }, [disableCalculatorUsage, fetchPossibleValues]);

  const constructCreditItemDto = (calculations: CalculationResult): CreditGroupItemDto => {
    const month = calculations.paymentPeriodParam.usedValue || 0;
    // advancePaymentParam is returned as integer representing percentage but dto expects a decimal value
    const advance = (calculations.advancePaymentParam.usedValue || 0) / 100;
    const monthlyInstalment = Math.round(calculations.monthlyInstalment);
    const payment = monthlyInstalment;
    return new CreditGroupItemDto(month, advance, payment);
  };

  const handleCalculationUpdate = (groupItems: Array<CreditGroupItemDto>, calculationParams: CalculationConfigDto) => {
    fetchPossibleValues(calculationParams.productId);
    props.handleUpdate(new CreditGroupDto(props.creditGroup.type, groupItems, calculationParams));
  };

  const updateCalculatorUsage = (use: boolean) => {
    if (!use) {
      props.handleUpdate(new CreditGroupDto(props.creditGroup.type, props.creditGroup.items));
    }
    setUseCalculator(use);
  };

  return (
    <TabPanel value={props.creditGroup.type}>
      <Grid container>
        <Tooltip title={disableCalculatorUsage ? t('userError.financeCalculation.calculationFailed') : ''}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  value={useCalculator}
                  checked={useCalculator}
                  disabled={disableCalculatorUsage}
                  onChange={(event, checked) => updateCalculatorUsage(checked)}
                />
              }
              label={t('fieldName.useCalculator')}
            />
          </FormGroup>
        </Tooltip>
      </Grid>
      {useCalculator && (
        <Paper sx={{ margin: '24px 0px', padding: '16px' }}>
          <InsuranceDetails
            cardContext={props.cardContext}
            insuranceConfigOptions={insuranceConfigOptions}
            financeGroup={props.creditGroup}
            domain={props.domain}
            productDisclaimer={productDisclaimer}
            enableCreditOptions
            // @ts-ignore
            handleUpdate={handleCalculationUpdate}
            constructItemDto={constructCreditItemDto}
            financeGroupCalculationParams={props.creditGroup.creditCalculationParams}
          />
        </Paper>
      )}
      <CreditGroupTable
        creditGroup={props.creditGroup}
        handleUpdate={props.handleUpdate}
        monthOptions={useCalculator ? paymentPeriodOptions : undefined}
        advanceOptions={useCalculator ? advancePaymentOptions : undefined}
      />
    </TabPanel>
  );
}
